<template>
    <el-dialog
        :visible="visible"
        width="600px"
        top="10vh"
        :show-close="false"
        :close-on-click-modal="false"
        custom-class="drawing-download-dialog"
        @close="onClose"
    >
        <div>
            <!-- Title bar -->
            <div class="ddd-title-bar">
                <i class="fa-solid fa-cloud-arrow-down ddd-title-icon"></i>
                <h5 class="ddd-title-text">{{ $t('msg.sithMesII.pds.drawingDownload.title') }}</h5>
                <span class="ddd-title-module">{{ moduleLabel }}</span>
                <div class="ddd-title-actions">
                    <span v-if="isBusy" class="ddd-spin ddd-spin-sm"></span>
                    <button type="button" class="ddd-close-btn" @click="onClose">&times;</button>
                </div>
            </div>

            <!-- Body -->
            <div class="ddd-body">
                <!-- Status hero -->
                <div class="ddd-hero">
                    <div class="ddd-hero-icon">
                        <span v-if="isBusy" class="ddd-spin ddd-spin-lg"></span>
                        <i v-else-if="isSuccess" class="fa-solid fa-circle-check" style="color:#28a745;font-size:40px;"></i>
                        <i v-else-if="isPartial" class="fa-solid fa-circle-exclamation" style="color:#e0a800;font-size:40px;"></i>
                        <i v-else-if="isFailed" class="fa-solid fa-circle-xmark" style="color:#dc0000;font-size:40px;"></i>
                    </div>
                    <div class="ddd-hero-text">
                        <div class="ddd-hero-title" :style="{ color: statusColor }">{{ statusTitle }}</div>
                        <div class="ddd-hero-sub">{{ statusSub }}</div>
                    </div>
                    <div class="ddd-hero-percent">
                        <div class="ddd-hero-percent-num" :style="{ color: statusColor }">{{ doneCount }} / {{ total }}</div>
                        <div class="ddd-hero-percent-sub">{{ $t('msg.sithMesII.pds.drawingDownload.filesUnit') }}</div>
                    </div>
                </div>

                <!-- Stage stepper -->
                <div class="ddd-stepper">
                    <div class="ddd-stepper-line"></div>
                    <div class="ddd-stepper-items">
                        <div v-for="st in stages" :key="st.num" class="ddd-stepper-item">
                            <div class="ddd-stepper-circle" :style="stageCircleStyle(st.state)">
                                <i v-if="st.state === 'done'" class="fa-solid fa-check"></i>
                                <i v-else-if="st.state === 'error'" class="fa-solid fa-xmark"></i>
                                <template v-else>{{ st.num }}</template>
                            </div>
                            <div class="ddd-stepper-label" :style="stageLabelStyle(st.state)">{{ st.label }}</div>
                        </div>
                    </div>
                </div>

                <!-- Summary -->
                <div v-if="summaryParts.length" class="ddd-summary">
                    <span v-for="(part, i) in summaryParts" :key="i" class="ddd-summary-part" :style="{ color: part.color }">
                        {{ part.text }}
                    </span>
                </div>

                <!-- Error alert -->
                <div v-if="errorLines.length" class="ddd-error-alert">
                    <i class="fa-solid fa-triangle-exclamation" style="color:#dc0000;font-size:14px;margin-top:2px;"></i>
                    <div class="ddd-error-alert-text">
                        <div v-for="(line, i) in errorLines" :key="i">{{ line }}</div>
                    </div>
                </div>

                <!-- Material list -->
                <div class="ddd-material-panel">
                    <div class="ddd-material-header">
                        <span class="ddd-material-header-title">{{ materialListTitle }}</span>
                        <span class="ddd-material-header-count">{{ doneCount }} / {{ total }}</span>
                    </div>
                    <div class="ddd-material-scroll mmf-scroll">
                        <div
                            v-for="(row, rowIndex) in rows"
                            :key="row.material + '_' + rowIndex"
                            class="ddd-material-row"
                            :style="{ background: rowBgFor(row.statusCode) }"
                        >
                            <div class="ddd-material-main">
                                <div class="ddd-material-line1">
                                    <span class="ddd-material-code">{{ row.material }}</span>
                                    <span v-if="row.root && hasBomComponents" class="ddd-root-tag">
                                        {{ $t('msg.sithMesII.pds.drawingDownload.rootTag') }}
                                    </span>
                                </div>
                                <div class="ddd-material-desc">{{ row.description }}</div>
                                <div v-if="row.remark" class="ddd-material-remark" :title="row.remark">{{ row.remark }}</div>
                                <div v-if="row.showVersions" class="ddd-material-versions">
                                    <span class="ddd-version-chip ddd-version-chip-new">
                                        {{ $t('msg.sithMesII.pds.drawingDownload.versionNew') }} {{ row.latestVersion }}
                                    </span>
                                    <span v-if="row.oldVersion" class="ddd-version-chip ddd-version-chip-old">
                                        {{ $t('msg.sithMesII.pds.drawingDownload.versionOld') }} {{ row.oldVersion }}
                                    </span>
                                </div>
                            </div>
                            <div class="ddd-material-status">
                                <span v-if="row.showSpinner" class="ddd-spin ddd-spin-xs"></span>
                                <span class="ddd-material-badge" :style="badgeStyleFor(row.statusCode)">{{ row.statusMsg }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="ddd-footer">
                <div class="ddd-footer-hint">
                    <i class="fa-regular fa-clock" style="margin-right:4px;"></i>{{ footerHint }}
                </div>
                <template v-if="isBusy">
                    <button type="button" class="ddd-btn ddd-btn-plain" @click="onClose">
                        {{ $t('msg.sithMesII.pds.drawingDownload.btn.cancel') }}
                    </button>
                </template>
                <template v-else-if="isFailed">
                    <button type="button" class="ddd-btn ddd-btn-plain" @click="onClose">
                        {{ $t('msg.sithMesII.pds.drawingDownload.btn.cancel') }}
                    </button>
                    <template v-if="canRetryFailed">
                        <!-- 不按本次刷新结果收起:打包从本地 material_drawing_info 取图,与 SAP
                             刷新成败解耦,刷新失败的物料仍可能有此前已入库的图纸 -->
                        <button type="button" class="ddd-btn ddd-btn-plain" @click="onDownloadAnyway">
                            <i class="fa-solid fa-download" style="margin-right:5px;"></i>
                            {{ $t('msg.sithMesII.pds.drawingDownload.btn.downloadAnyway') }}
                        </button>
                        <button type="button" class="ddd-btn ddd-btn-primary" @click="onRetry">
                            <i class="fa-solid fa-rotate-right" style="margin-right:5px;"></i>
                            {{ $t('msg.sithMesII.pds.drawingDownload.btn.retryFailed') }}
                        </button>
                    </template>
                    <button v-else type="button" class="ddd-btn ddd-btn-primary" @click="onRetry">
                        <i class="fa-solid fa-rotate-right" style="margin-right:5px;"></i>
                        {{ $t('msg.sithMesII.pds.drawingDownload.btn.retry') }}
                    </button>
                </template>
                <template v-else-if="isSuccess">
                    <button type="button" class="ddd-btn ddd-btn-plain" @click="startFlow">
                        <i class="fa-solid fa-rotate-right" style="margin-right:5px;"></i>
                        {{ $t('msg.sithMesII.pds.drawingDownload.btn.redownload') }}
                    </button>
                    <button type="button" class="ddd-btn ddd-btn-primary" @click="onClose">
                        {{ $t('msg.sithMesII.pds.drawingDownload.btn.done') }}
                    </button>
                </template>
                <template v-else-if="isPartial">
                    <button type="button" class="ddd-btn ddd-btn-primary" @click="onClose">
                        {{ $t('msg.sithMesII.pds.drawingDownload.btn.done') }}
                    </button>
                </template>
            </div>
        </div>
    </el-dialog>
</template>

<script>
import { mapState } from "vuex";
import emCommonUtilMixin from "@/mixin/em/common/stuffs/utils/emCommonUtilMixin";
import { startJob, pollStatus, packJob, retryJob, cancelJob } from "@/services/drawing-download-service";

const ITEM = {
    WAITING: 1, QUERYING: 2, LATEST: 3, DOWNLOADING: 4, UPDATED: 5, LOCAL_ONLY: 6, PARTIAL_STORED: 7,
    SAP_FAILED: 20, EMPTY: 21, STORE_FAILED: 22,
};
const JOB = {
    NEW: 1, RUNNING: 2, SUCCESS: 3, PACKING: 4, PACKED: 5,
    PARTIAL_FAILED: 21, CANCELED: 22, PACK_FAILED: 23,
};

// 停滞阈值(秒)。后端 DrawingDownloadJobStore.get 每次命中都会续期 Redis TTL,
// 也就是说只要弹窗还在轮询,任务状态就永远不会过期 —— 一旦 MQ 消息丢失,任务会永久停在
// NEW/RUNNING,弹窗会无限转圈。服务端不会主动报这种"活着但没进展",只能由前端按
// vo.stalledSeconds(服务端算好的"距上次进展多少秒")自行判超时。
//
// 两段阈值都是"多久没有生命迹象",不是"跑了多久":
// - 刷新段每处理完一个物料都会回写进展,stalledSeconds 随之清零;
// - 打包段中途没有细粒度进展可报,改由服务端每 30 秒打一次心跳
//   (DrawingDownloadJobAggregatorImpl#packHeartbeat)顶住 stalledSeconds,
//   所以这里不必再为"大包正常耗时"留余量 —— 只要还在打包,它就一直接近 0。
//   取 120 秒 = 容得下丢 3 拍心跳(Redis 抖动),又能在打包线程真死掉后两分钟内报出来。
// 打包"活着但卡死"(如 feign 死等)心跳照跳、这里判不出来,由服务端的打包总时长上限兜底。
const STALL_LIMIT_REFRESH_SECONDS = 300;
const STALL_LIMIT_PACK_SECONDS = 120;

// 这些失败阶段只能整单重开,不能在原 jobCode 上原地重跑:
// submit 压根没有 job;canceled 的 job 已经作废;stalled* 是处理链路中断
// (MQ 消息丢失 / 打包线程死亡),在同一个 job 上再下一次单也唤不醒它。
const RESTART_STAGES = ["submit", "canceled", "stalledRefresh", "stalledPack"];

const I18N = "msg.sithMesII.pds.drawingDownload.";
const ITEM_STATUS_I18N = {
    [ITEM.WAITING]: I18N + "itemStatus.waiting",
    [ITEM.QUERYING]: I18N + "itemStatus.querying",
    [ITEM.LATEST]: I18N + "itemStatus.latest",
    [ITEM.DOWNLOADING]: I18N + "itemStatus.downloading",
    [ITEM.UPDATED]: I18N + "itemStatus.updated",
    [ITEM.LOCAL_ONLY]: I18N + "itemStatus.localOnly",
    [ITEM.PARTIAL_STORED]: I18N + "itemStatus.partialStored",
    [ITEM.SAP_FAILED]: I18N + "itemStatus.sapFailed",
    [ITEM.EMPTY]: I18N + "itemStatus.empty",
    [ITEM.STORE_FAILED]: I18N + "itemStatus.storeFailed",
};
// PARTIAL_STORED(部分文件下载失败)算可下载:已入库的图纸要照常进 zip,
// 否则整单会被判为无可下载项而不给自动打包。但它不进 FAILED_CODES ——
// 该物料没有"整体失败",重试整物料也补不回单个坏文件,只在汇总里用警示色提示 zip 内有缺文件。
const DOWNLOADABLE = [ITEM.UPDATED, ITEM.LATEST, ITEM.LOCAL_ONLY, ITEM.PARTIAL_STORED];
const FAILED_CODES = [ITEM.SAP_FAILED, ITEM.STORE_FAILED];

export default {
    name: "DrawingDownloadDialog",
    mixins: [emCommonUtilMixin],
    data() {
        // phase: idle | starting | running | packing | success | partial | failed
        // failStage 仅在 phase === 'failed' 时有意义,决定红叉钉在哪一段:
        //   submit / refresh / pack / canceled / stalledRefresh / stalledPack
        // packError: 打包失败的具体原因(轮询到 PACK_FAILED 时取 vo.packErrorMsg;
        //            取件(GET /file)失败时取 downloadWithHeader 回传的 message)
        // packPending: 已 POST /pack 但还没在轮询里看到 PACKING/PACKED —— 这段窗口里
        //            读到的 SUCCESS / PARTIAL_FAILED / 上一轮的 PACK_FAILED 都是下单前的
        //            旧状态,不能拿来判成败(否则重试打包会被上一轮的 PACK_FAILED 秒杀)
        // stalledMinutes: 判定停滞时的无进展分钟数,只用于提示文案
        return {
            jobCode: null, vo: null, phase: "idle", failStage: null, packError: null,
            packPending: false, stalledMinutes: 0, timer: null,
        };
    },
    created() {
        // 轮询请求序号(非响应式, 不参与渲染): 每发起一次 status 请求就 +1,
        // 响应回来时号对不上就整份丢弃 —— 见 poll() 的注释
        this.$_pollSeq = 0;
    },
    computed: {
        ...mapState("drawingDownload", ["visible", "materials", "scope", "module"]),
        isPDS() { return this.scope === 1; },
        rows() {
            if (!this.vo) return [];
            return this.vo.items.map(it => ({
                material: it.material,
                root: it.root,
                description: it.description,
                statusCode: it.statusCode,
                statusMsg: this.itemStatusText(it),
                remark: it.remark,
                latestVersion: it.latestVersion,
                oldVersion: it.oldVersion,
                showSpinner: it.statusCode === ITEM.QUERYING || it.statusCode === ITEM.DOWNLOADING,
                showVersions: this.isPDS && DOWNLOADABLE.indexOf(it.statusCode) >= 0,
            }));
        },
        items() { return this.vo ? this.vo.items : []; },
        total() { return this.vo ? this.vo.total : this.materials.length; },
        doneCount() { return this.vo ? this.vo.doneCount : 0; },
        isBusy() { return ["starting", "running", "packing"].indexOf(this.phase) >= 0; },
        isRunning() { return this.phase === "running"; },
        // 后端 MQ 消费并发为 3,超出的任务停在 NEW 状态排队;此时回显排位而非"正在获取"
        isQueued() { return this.isRunning && !!this.vo && this.vo.jobStatusCode === JOB.NEW; },
        queueAheadCount() {
            return this.isQueued && this.vo.queueAheadCount != null ? this.vo.queueAheadCount : 0;
        },
        isPacking() { return this.phase === "packing"; },
        isStalled() {
            return this.isFailed
                && (this.failStage === "stalledRefresh" || this.failStage === "stalledPack");
        },
        isSuccess() { return this.phase === "success"; },
        isPartial() { return this.phase === "partial"; },
        isFailed() { return this.phase === "failed"; },
        anyDownloadable() { return this.items.some(i => DOWNLOADABLE.indexOf(i.statusCode) >= 0); },
        anyFailed() { return this.items.some(i => FAILED_CODES.indexOf(i.statusCode) >= 0); },
        // 展开上线前提交、状态还留在 Redis 里的老任务没有 root 标记,那时整份清单就是用户选的物料
        anyRoot() { return this.items.some(i => i.root); },
        // 只有入口物料(用户自己选中的那些)失败才拦住自动打包。清单里的其余物料是后端按 BOM
        // 展开出来的子件,它们是"顺带多刷的一份":展开前压根不参与刷新,照样能打包出 zip,
        // 现在因为一个子件 SAP 抖动就把整单判死、逼用户手动点一次「仍然下载已有图纸」,
        // 反而比展开前更难用 —— 树越大越必然踩中。子件失败改由 partial(黄色)+ errorLines 呈现。
        rootFailed() {
            return this.items.some(i => this.isRootItem(i) && FAILED_CODES.indexOf(i.statusCode) >= 0);
        },
        // 有失败物料就该给「重试失败项」出口,与红叉钉在哪一段无关:打包失败后仍钉着
        // failStage='pack' 时,若只按 failStage 给按钮,SAP 失败项就再也没有重试入口。
        // RESTART_STAGES 里的失败只能整单重开(见常量注释),给不出"重试失败项"这种局部出口。
        canRetryFailed() {
            return this.anyFailed && RESTART_STAGES.indexOf(this.failStage) < 0;
        },
        counts() {
            return this.items.reduce((acc, i) => {
                if (i.statusCode === ITEM.UPDATED) acc.updated++;
                else if (i.statusCode === ITEM.LATEST) acc.latest++;
                else if (i.statusCode === ITEM.LOCAL_ONLY) acc.localOnly++;
                else if (i.statusCode === ITEM.PARTIAL_STORED) acc.partialStored++;
                else if (i.statusCode === ITEM.EMPTY) acc.empty++;
                else if (i.statusCode === ITEM.SAP_FAILED) acc.sapFailed++;
                else if (i.statusCode === ITEM.STORE_FAILED) acc.storeFailed++;
                return acc;
            }, { updated: 0, latest: 0, localOnly: 0, partialStored: 0, empty: 0, sapFailed: 0, storeFailed: 0 });
        },
        summaryParts() {
            const c = this.counts;
            return [
                { n: c.updated, key: "summary.updated", color: "#1b7a34" },
                { n: c.latest, key: "summary.latest", color: "#1E5274" },
                { n: c.localOnly, key: "summary.localOnly", color: "#0f6674" },
                // 与 empty 同一警示色:不是失败,但用户必须看到 zip 里有缺失文件
                { n: c.partialStored, key: "summary.partialStored", color: "#856404" },
                { n: c.empty, key: "summary.empty", color: "#856404" },
                { n: c.sapFailed, key: "summary.sapFailed", color: "#dc0000" },
                { n: c.storeFailed, key: "summary.storeFailed", color: "#dc0000" },
            ].filter(p => p.n > 0)
                .map(p => ({ color: p.color, text: this.$t(I18N + p.key, { 0: p.n }) }));
        },
        // 清单里除了用户选中的物料,还有后端按 BOM 展开出来的子件。要求"既有 root 又有非 root",
        // 老任务(整份清单都没有 root 标记)才不会被误报成含子件。
        hasBomComponents() { return this.anyRoot && this.items.some(i => !i.root); },
        materialListTitle() {
            return this.$t(I18N + (this.hasBomComponents ? "materialListWithBom" : "materialList"));
        },
        moduleLabel() {
            return this.module ? this.$t(I18N + "module", { 0: this.module }) : "";
        },
        statusColor() {
            if (this.isSuccess) return '#28a745';
            if (this.isPartial) return '#e0a800';
            if (this.isFailed) return '#dc0000';
            return '#1E5274';
        },
        statusTitle() {
            if (this.isSuccess) return this.$t(I18N + "status.success.title");
            if (this.isPartial) return this.$t(I18N + "status.partial.title");
            if (this.isFailed) return this.$t(I18N + "status.failed.title");
            if (this.isPacking) return this.$t(I18N + "status.packing.title");
            if (this.isQueued) return this.$t(I18N + "status.queued.title");
            return this.$t(I18N + "status.running.title");
        },
        statusSub() {
            if (this.isSuccess) {
                return this.$t(I18N + (this.anyDownloadable ? "status.success.sub" : "status.success.subEmpty"));
            }
            if (this.isPartial) return this.$t(I18N + "status.partial.sub");
            if (this.isFailed) {
                const byStage = {
                    submit: "subSubmit", refresh: "subRefresh", pack: "subPack", canceled: "subCanceled",
                    stalledRefresh: "subStalledRefresh", stalledPack: "subStalledPack",
                };
                return this.$t(I18N + "status.failed." + (byStage[this.failStage] || "subRefresh"));
            }
            if (this.isPacking) return this.$t(I18N + "status.packing.sub");
            if (this.isQueued) {
                return this.queueAheadCount > 0
                    ? this.$t(I18N + "status.queued.sub", { 0: this.queueAheadCount })
                    : this.$t(I18N + "status.queued.subFirst");
            }
            return this.$t(I18N + "status.running.sub");
        },
        footerHint() {
            if (this.isSuccess || this.isPartial) return this.$t(I18N + "footerHint.success");
            // 停滞失败给不出"重试失败项/下载现存图纸"这两个出口,不能沿用 footerHint.failed
            if (this.isStalled) return this.$t(I18N + "footerHint.stalled");
            if (this.isFailed) return this.$t(I18N + "footerHint.failed");
            if (this.isPacking) return this.$t(I18N + "footerHint.packing");
            if (this.isQueued) return this.$t(I18N + "footerHint.queued");
            return this.$t(I18N + "footerHint.running");
        },
        // 按失败类型分组列出物料号,替代"只报第一个失败项"。阶段性错误只作前缀,
        // 不顶掉逐物料明细 —— partial(下载了已有图纸但有刷新失败项)同样要列出来。
        errorLines() {
            if (!this.isFailed && !this.isPartial) return [];
            if (this.failStage === "submit") return [this.$t(I18N + "error.submit")];
            const lines = [];
            if (this.failStage === "pack") {
                lines.push(this.$t(I18N + "error.pack"));
                if (this.packError) lines.push(this.$t(I18N + "error.packReason", { 0: this.packError }));
            } else if (this.failStage === "canceled") {
                lines.push(this.$t(I18N + "error.canceled"));
            } else if (this.isStalled) {
                const key = this.failStage === "stalledPack" ? "error.stalledPack" : "error.stalledRefresh";
                lines.push(this.$t(I18N + key, { 0: this.stalledMinutes }));
            }
            const grouped = [];
            [
                { code: ITEM.SAP_FAILED, key: "error.sapFailed" },
                { code: ITEM.STORE_FAILED, key: "error.storeFailed" },
            ].forEach(g => {
                const materials = this.items.filter(i => i.statusCode === g.code).map(i => i.material);
                if (materials.length) {
                    grouped.push(this.$t(I18N + g.key, { 0: materials.length, 1: materials.join("、") }));
                }
            });
            // hint 讲的是"重试失败项 / 下载现存图纸"这两个出口,拿不到出口时(整单重开)不该给
            if (grouped.length) {
                lines.push(...grouped);
                if (this.canRetryFailed || this.isPartial) lines.push(this.$t(I18N + "error.hint"));
            }
            return lines;
        },
        stageStates() {
            if (this.isFailed) {
                if (this.failStage === "submit") return ['error', 'wait', 'wait'];
                if (this.failStage === "refresh" || this.failStage === "stalledRefresh") {
                    return ['done', 'error', 'wait'];
                }
                // 打包失败时刷新段是否为绿取决于有无失败物料,不能一律涂绿
                if (this.failStage === "pack" || this.failStage === "stalledPack") {
                    return ['done', this.anyFailed ? 'error' : 'done', 'error'];
                }
                return ['done', 'error', 'wait'];   // canceled
            }
            if (this.isSuccess || this.isPartial) return ['done', 'done', 'done'];
            if (this.isPacking) return ['done', 'done', 'active'];
            if (this.phase === "starting" || !this.vo) return ['active', 'wait', 'wait'];
            // 排队中:提交已完成,刷新尚未开始,不给"刷新图纸"段点亮进行中动画
            if (this.isQueued) return ['done', 'wait', 'wait'];
            return ['done', 'active', 'wait'];
        },
        stages() {
            const keys = ['stage.submit', 'stage.refresh', 'stage.pack'];
            return keys.map((k, i) => ({ num: i + 1, label: this.$t(I18N + k), state: this.stageStates[i] }));
        },
    },
    watch: {
        visible(v) { if (v) this.startFlow(); else this.teardown(); },
    },
    methods: {
        // 老任务没有 root 标记,整份清单都按入口物料算(见 anyRoot)
        isRootItem(item) { return item.root || !this.anyRoot; },
        itemStatusText(it) {
            const key = ITEM_STATUS_I18N[it.statusCode];
            // 后端 statusMsg(枚举中文)仅作未知 code 的兜底
            return key ? this.$t(key) : it.statusMsg;
        },
        async startFlow() {
            this.teardown(false);
            this.vo = null;
            this.failStage = null;
            this.packError = null;
            this.packPending = false;
            this.stalledMinutes = 0;
            this.phase = "starting";
            try {
                this.jobCode = await startJob(this.$store, this.materials, this.scope);
                this.phase = "running";
                this.poll();
            } catch (e) {
                this.failStage = "submit";
                this.phase = "failed";
            }
        },
        poll() {
            this.stopPolling();
            this.timer = setInterval(async () => {
                // 先占号再干活: 只有序号仍是最新的那次请求有资格回写 vo / 驱动状态机。
                // setInterval + async 回调不会互相等待, 后端慢时(打包段尤其容易)多个 status
                // 请求会重叠在途, 先发后到的旧 vo 会盖掉新 vo —— 表现为进度倒退、
                // 甚至已经 PACKED 了又被旧的 SUCCESS 拉回去重新触发一次打包。
                // stopPolling() 也会占号, 所以停轮询之后回来的响应同样作废。
                const seq = ++this.$_pollSeq;
                try {
                    const vo = await pollStatus(this.$store, this.jobCode);
                    if (seq !== this.$_pollSeq) return;   // 过期响应, 整份丢弃
                    this.vo = vo;
                    this.handleStatus(vo);
                } catch (e) { /* 单次轮询失败忽略,下次继续 */ }
            }, 1000);
        },
        stopPolling() {
            if (this.timer) { clearInterval(this.timer); this.timer = null; }
            // 占掉一个新号, 让此刻还在途的响应回来时对不上号而被丢弃
            this.$_pollSeq++;
        },
        // 状态机唯一入口:phase 的推进完全由后端 jobStatusCode 驱动,
        // 不再由"某个 HTTP 请求回调了"来决定(打包已改为异步任务,那个回调不再代表打包结束)
        handleStatus(vo) {
            const code = vo.jobStatusCode;
            // 看到打包段状态,说明 POST /pack 已经生效,之后读到的 PACK_FAILED 才是本次打包的结果
            if (code === JOB.PACKING || code === JOB.PACKED) this.packPending = false;
            if (this.packPending && code !== JOB.CANCELED) {
                // 打包已下单但后端状态还没翻过来:读到的 SUCCESS / PARTIAL_FAILED / 上一轮的
                // PACK_FAILED 都是下单前的旧状态,不能据此判成败,只当"打包中"继续等。
                // 这里刻意不做停滞判定:vo.stalledSeconds 也来自下单前的旧状态,是从上一次刷新
                // 进展算起的。弹窗停在终态时轮询已停、lastProgressAt 不再推进,用户隔十几分钟
                // 再点「仍然下载已有图纸」,首帧就会读到一个早已超过阈值的 stalledSeconds,
                // 当场误判停滞。下单本身失败由 packJob(...).catch 兜住;一旦看到 PACKING,
                // 后端 pack() 已写入新的 lastProgressAt,由下面的正常分支接管判定。
                this.phase = "packing";
                return;
            }
            switch (code) {
                case JOB.CANCELED:
                    // state 不存在时后端也返回 CANCELED + jobStatusMsg="任务状态已过期"
                    this.stopPolling();
                    this.failStage = "canceled";
                    this.phase = "failed";
                    if (vo.jobStatusMsg) this.$message.warning(vo.jobStatusMsg);
                    return;
                case JOB.PACK_FAILED:
                    this.stopPolling();
                    this.failStage = "pack";
                    this.packError = vo.packErrorMsg || null;
                    this.phase = "failed";
                    this.$message.warning(this.$t(I18N + "packFail"));
                    return;
                case JOB.PACKED:
                    // zip 已在服务端就绪,这时的 GET /file 是秒回的取件动作
                    this.stopPolling();
                    this.triggerFileDownload();
                    return;
                case JOB.PACKING:
                    this.phase = "packing";
                    this.checkStalled(vo, "stalledPack", STALL_LIMIT_PACK_SECONDS);
                    return;
                case JOB.SUCCESS:
                case JOB.PARTIAL_FAILED:
                    // 刷新段结束,决定要不要进打包段(轮询是否继续由 onJobFinished 决定)
                    this.onJobFinished();
                    return;
                default:
                    // NEW / RUNNING:还在排队或刷新中
                    this.checkStalled(vo, "stalledRefresh", STALL_LIMIT_REFRESH_SECONDS);
            }
        },
        // 停滞判定:阈值与两段的差异见 STALL_LIMIT_* 常量注释。
        // stalledSeconds 由服务端算好直接给,不用前端时钟推算(跨时区/系统时间不准都会算错)。
        checkStalled(vo, stage, limitSeconds) {
            if (vo.stalledSeconds == null || vo.stalledSeconds <= limitSeconds) return false;
            this.stopPolling();
            this.stalledMinutes = Math.floor(vo.stalledSeconds / 60);
            this.failStage = stage;
            this.phase = "failed";
            this.$message.warning(this.$t(I18N + "stalled"));
            return true;
        },
        onJobFinished() {
            // 入口物料失败时不自动打包:此前"文件已下到本地、界面却说下载失败"是反直觉的来源。
            // 用户可从 footer 的「仍然下载已有图纸」自行取用系统现存图纸。
            // 子件失败不走这条路,见 rootFailed 的注释。
            if (this.rootFailed) {
                this.stopPolling();
                this.failStage = "refresh";
                this.phase = "failed";
                return;
            }
            if (!this.anyDownloadable) {
                // 全 EMPTY:不打包、不产出空 zip
                this.stopPolling();
                this.phase = "success";
                this.$message.warning(this.$t(I18N + "noDrawing"));
                return;
            }
            this.requestPack();
        },
        onDownloadAnyway() { this.requestPack(); },
        // 打包第一段:下单。POST /pack 立即返回(幂等),打包在后端异步进行,
        // 成败一律由轮询看 PACKING → PACKED / PACK_FAILED 得知。
        requestPack() {
            this.failStage = null;
            this.packError = null;
            this.stalledMinutes = 0;
            this.packPending = true;
            // 下单瞬间先本地给一次"打包中"只为立刻锁住按钮、避免重复点击;
            // 此后 phase 完全由 jobStatusCode 驱动(PACKING 维持、PACKED 取件、PACK_FAILED 报错),
            // 不再像原来那样由 GET /file 的回调来决定打包何时结束
            this.phase = "packing";
            packJob(this.$store, this.jobCode).catch(() => {
                // 连单都没下成(网关/鉴权层失败),没有后续状态可等,直接判打包失败
                this.packPending = false;
                this.stopPolling();
                this.failStage = "pack";
                this.phase = "failed";
                this.$message.warning(this.$t(I18N + "packFail"));
            });
            this.poll();
        },
        // 打包第二段:取件。只在 PACKED 后调用,不再承担"等后端打包"的职责
        triggerFileDownload() {
            const hadFailure = this.anyFailed;
            this.failStage = null;
            this.packError = null;
            this.phase = "packing";
            const url = process.env.VUE_APP_SITHMES_II_URL
                + "/materialDrawing/download/job/" + this.jobCode + "/file";
            this.downloadWithHeader(url, "BOM Drawing.zip", (success, errorMsg) => {
                if (!success) {
                    this.failStage = "pack";
                    this.packError = errorMsg || null;
                    this.phase = "failed";
                    this.$message.warning(this.$t(I18N + "packFail"));
                    return;
                }
                this.phase = hadFailure ? "partial" : "success";
            });
        },
        onRetry() {
            // 没有 job,或失败在只能整单重开的阶段(见 RESTART_STAGES)
            if (!this.jobCode || RESTART_STAGES.indexOf(this.failStage) >= 0) {
                this.startFlow();
                return;
            }
            // 没有失败物料时,打包失败就是单纯的打包问题
            if (!this.anyFailed && this.failStage === "pack") {
                // zip 其实已经打好、只是取件那一下没成(断网等),直接重取,不必重新打包
                if (this.vo && this.vo.jobStatusCode === JOB.PACKED) this.triggerFileDownload();
                else this.requestPack();    // 后端幂等,允许从 PACK_FAILED 重新打包
                return;
            }
            // 有失败物料:回到刷新段重跑失败项,跑完由状态机自己再走一遍打包
            retryJob(this.$store, this.jobCode).then(() => {
                this.failStage = null;
                this.packError = null;
                this.packPending = false;
                this.stalledMinutes = 0;
                this.phase = "running";
                this.poll();
            });
        },
        onClose() {
            if (this.isRunning || this.phase === "starting") cancelJob(this.$store, this.jobCode);
            this.$store.dispatch("drawingDownload/close");
        },
        teardown(resetJob = true) {
            this.stopPolling();
            if (resetJob) { this.jobCode = null; }
        },
        stageCircleStyle(state) {
            if (state === 'done') return 'background:#1E5274;color:#fff;';
            if (state === 'active') return 'background:#fff;color:#1E5274;border:2px solid #1E5274;animation:ddl-pulse 1.4s infinite;';
            if (state === 'error') return 'background:#dc0000;color:#fff;';
            return 'background:#fff;color:#adb5bd;border:2px solid #dee2e6;';
        },
        stageLabelStyle(state) {
            if (state === 'done' || state === 'active') return 'color:#1E5274;font-weight:700;';
            if (state === 'error') return 'color:#dc0000;font-weight:700;';
            return 'color:#adb5bd;';
        },
        rowBgFor(statusCode) {
            if (statusCode === ITEM.QUERYING || statusCode === ITEM.DOWNLOADING) return '#f5f9fc';
            if (FAILED_CODES.indexOf(statusCode) >= 0) return '#fdf3f3';
            return '#fff';
        },
        badgeStyleFor(statusCode) {
            switch (statusCode) {
                case ITEM.UPDATED: return 'background:rgba(40,167,69,.13);color:#1b7a34;';
                case ITEM.LATEST: return 'background:rgba(30,82,116,.12);color:#1E5274;';
                case ITEM.LOCAL_ONLY: return 'background:rgba(23,162,184,.13);color:#0f6674;';
                case ITEM.QUERYING:
                case ITEM.DOWNLOADING: return 'background:rgba(30,82,116,.12);color:#1E5274;';
                case ITEM.PARTIAL_STORED:
                case ITEM.EMPTY: return 'background:#fff3cd;color:#856404;';
                case ITEM.SAP_FAILED:
                case ITEM.STORE_FAILED: return 'background:rgba(220,0,0,.12);color:#dc0000;';
                default: return 'background:#f1f3f5;color:#8a97a1;';
            }
        },
    },
    beforeDestroy() { this.teardown(); },
};
</script>

<style scoped>
::v-deep .drawing-download-dialog {
    border-radius: 6px;
    overflow: hidden;
}

::v-deep .drawing-download-dialog .el-dialog__header,
::v-deep .drawing-download-dialog .el-dialog__body {
    padding: 0;
}

.ddd-title-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: .55rem .85rem;
    background: #fff;
    border-bottom: 1px solid #dee2e6;
}

.ddd-title-icon {
    color: #1E5274;
    font-size: 15px;
}

.ddd-title-text {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: #000;
    line-height: 1.4;
}

.ddd-title-module {
    margin-left: 6px;
    font-size: 11px;
    color: #6c757d;
    font-weight: 400;
}

.ddd-title-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 2px;
}

.ddd-close-btn {
    border: none;
    background: transparent;
    color: #6c757d;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
    padding: 0 4px;
}

.ddd-body {
    padding: 1rem 1.1rem 1.1rem;
}

.ddd-hero {
    display: flex;
    align-items: center;
    gap: .85rem;
    margin-bottom: .9rem;
}

.ddd-hero-icon {
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ddd-hero-text {
    min-width: 0;
}

.ddd-hero-title {
    font-size: 1.15rem;
    font-weight: 700;
    line-height: 1.3;
}

.ddd-hero-sub {
    font-size: .82rem;
    color: #6c757d;
    margin-top: 1px;
}

.ddd-hero-percent {
    margin-left: auto;
    text-align: right;
    flex: 0 0 auto;
}

.ddd-hero-percent-num {
    font-size: 1.4rem;
    font-weight: 900;
    line-height: 1;
}

.ddd-hero-percent-sub {
    font-size: .72rem;
    color: #788791;
    letter-spacing: .02em;
    white-space: nowrap;
}

.ddd-stepper {
    position: relative;
    margin: 0 6px 1.1rem;
}

.ddd-stepper-line {
    position: absolute;
    top: 13px;
    left: 13px;
    right: 13px;
    height: 2px;
    background: #dee2e6;
}

.ddd-stepper-items {
    position: relative;
    display: flex;
    justify-content: space-between;
}

.ddd-stepper-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    width: 64px;
}

.ddd-stepper-circle {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
}

.ddd-stepper-label {
    font-size: .68rem;
    text-align: center;
    line-height: 1.2;
}

.ddd-summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: .35rem .75rem;
    margin: 0 0 .9rem;
    font-size: .76rem;
    font-weight: 700;
}

.ddd-summary-part + .ddd-summary-part::before {
    content: "·";
    color: #adb5bd;
    font-weight: 400;
    margin-right: .75rem;
}

.ddd-error-alert {
    display: flex;
    gap: .5rem;
    background: #fbe6e6;
    border: 1px solid #f1c2c2;
    border-radius: 4px;
    padding: .55rem .7rem;
    margin-bottom: .9rem;
}

.ddd-error-alert-text {
    font-size: .8rem;
    color: #8a1f1f;
    line-height: 1.45;
}

.ddd-material-panel {
    border: 1px solid #e3e8ec;
    border-radius: 5px;
    overflow: hidden;
}

.ddd-material-header {
    display: flex;
    align-items: center;
    padding: .4rem .7rem;
    background: #ebf0f8;
    border-bottom: 1px solid #dee2e6;
}

.ddd-material-header-title {
    font-size: .74rem;
    font-weight: 700;
    color: #1E5274;
    letter-spacing: .02em;
}

.ddd-material-header-count {
    margin-left: auto;
    font-size: .68rem;
    color: #6c757d;
    font-family: ui-monospace, Menlo, Consolas, monospace;
}

.ddd-material-scroll {
    max-height: 230px;
    overflow-y: auto;
}

.ddd-material-row {
    display: flex;
    align-items: flex-start;
    gap: .6rem;
    padding: .5rem .7rem;
    border-bottom: 1px solid #eef1f4;
}

.ddd-material-main {
    flex: 1;
    min-width: 0;
}

.ddd-material-line1 {
    display: flex;
    align-items: center;
    gap: .4rem;
    flex-wrap: wrap;
}

.ddd-material-code {
    font-family: ui-monospace, Menlo, Consolas, monospace;
    font-weight: 700;
    font-size: .82rem;
    color: #212529;
}

.ddd-root-tag {
    font-size: .62rem;
    line-height: 1;
    padding: 2px 5px;
    border-radius: 3px;
    background: #eef2f6;
    color: #1E5274;
    border: 1px solid #dde5eb;
    white-space: nowrap;
}

.ddd-material-remark {
    font-size: .72rem;
    color: #dc0000;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ddd-material-desc {
    font-size: .76rem;
    color: #6c757d;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ddd-material-versions {
    display: flex;
    gap: .3rem;
    margin-top: 4px;
    flex-wrap: wrap;
}

.ddd-version-chip {
    font-size: .66rem;
    padding: 1px 6px;
    border-radius: 3px;
}

.ddd-version-chip-new {
    background: #1E5274;
    color: #fff;
}

.ddd-version-chip-old {
    background: #eef2f6;
    color: #6c757d;
    border: 1px solid #dde5eb;
}

.ddd-material-status {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: .35rem;
    padding-top: 1px;
}

.ddd-material-badge {
    font-size: .7rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 3px;
    white-space: nowrap;
}

.ddd-footer {
    display: flex;
    align-items: center;
    gap: .5rem;
    padding: .7rem .9rem;
    background: #f7f9fb;
    border-top: 1px solid #dee2e6;
}

.ddd-footer-hint {
    font-size: .72rem;
    color: #788791;
    margin-right: auto;
}

.ddd-btn {
    font-weight: 700;
    font-size: .82rem;
    border-radius: 3px;
    padding: .28rem .8rem;
    cursor: pointer;
}

.ddd-btn-plain {
    border: 1px solid #6c757d;
    background: #fff;
    color: #6c757d;
}

.ddd-btn-primary {
    border: 1px solid #1E5274;
    background: #1E5274;
    color: #fff;
    padding: .28rem .95rem;
}

.ddd-spin {
    border: 2px solid #1E5274;
    border-right-color: transparent;
    border-radius: 50%;
    display: inline-block;
    animation: ddl-spin .75s linear infinite;
}

.ddd-spin-sm {
    width: 14px;
    height: 14px;
    margin-right: 6px;
}

.ddd-spin-lg {
    width: 34px;
    height: 34px;
    border-width: 4px;
    border-color: #d6e0e8;
    border-top-color: #1E5274;
}

.ddd-spin-xs {
    width: 11px;
    height: 11px;
}

@keyframes ddl-spin {
    to { transform: rotate(360deg); }
}

@keyframes ddl-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(30, 82, 116, .35); }
    50% { box-shadow: 0 0 0 6px rgba(30, 82, 116, 0); }
}
</style>
